import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { ComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, TimeoutError, forkJoin, interval, of } from 'rxjs';
import { tap } from 'rxjs/internal/operators/tap';
import { catchError, take, withLatestFrom } from 'rxjs/operators';
import { PublisherViewModel } from 'src/app/models/publisher.model';
import { Payment, PaymentService, PaymentViewModel } from 'src/app/services/payment.service';
import { StudentPaymentsStore } from '../../pages/student-payments/student-payments.store';
import * as fromStore from '../../store';
import { AddPublishers, checkIntentSuccess, getAllPublisherViewModels, selectAllPaymentIntents } from '../../store';
import { PaymentIntentTracking } from '../../store/reducers/payment-intent.reducer';

export interface StudentPaymentState {
  payment: Payment;
  paymentIntents: PaymentIntentTracking[];
  paymentViewModel: PaymentViewModel;
  emptyTemplate: string;
  firstLoadErrorType: string;
  publisherViewModels: PublisherViewModel[];
}

@Injectable()
export class StudentPaymentStore extends ComponentStore<StudentPaymentState> {
  constructor(
    private paymentService: PaymentService,
    private studentPaymentsStore: StudentPaymentsStore,
    private store: Store<fromStore.AppState>,
    private toastController: ToastController,
    private actions$: Actions
  ) {
    super({
      payment: null,
      paymentIntents: [],
      paymentViewModel: null,
      emptyTemplate: '',
      firstLoadErrorType: '',
      publisherViewModels: []
    });

    this.store.select(selectAllPaymentIntents).subscribe((paymentIntents) => {
      this.setState((state) => {
        return {
          ...state,
          paymentIntents
        };
      });
    });

    this.store.select(getAllPublisherViewModels).subscribe((publisherViewModels: PublisherViewModel[]) => {
      this.setState((state) => {
        return {
          ...state,
          publisherViewModels
        };
      });
    });

    this.actions$.pipe(ofType(checkIntentSuccess)).subscribe((action) => {
      if (action.payment.id === this.get().payment.id && action.payment.studentId === this.get().payment.studentId) {
        this.updatePayment({ payment: action.payment });
      }
    });
  }

  readonly vm$ = this.select(this.state$, (state) => {
    let payment: Payment = state.payment;
    let paymentViewModel: PaymentViewModel = state.paymentViewModel;

    if (state.payment) {
      payment = {
        ...state.payment,
        paymentBeingConfirmed:
          state.paymentIntents.findIndex(
            paymentIntent =>
              paymentIntent?.paymentId === state.payment?.id && paymentIntent?.studentId === state.payment?.studentId
          ) !== -1
      };
    }

    if (state.paymentViewModel) {
      paymentViewModel = {
        ...state.paymentViewModel,
        contact: state.publisherViewModels.find(publisherViewModel => publisherViewModel.id === payment.contact.id),
        paymentBeingConfirmed:
          state.paymentIntents.findIndex(
            paymentIntent =>
              paymentIntent?.paymentId === state.payment?.id && paymentIntent?.studentId === state.payment?.studentId
          ) !== -1
      };
    }

    return {
      ...state,
      payment,
      paymentViewModel
    };
  });

  readonly getPayment$ = this.select(this.vm$, vm => vm.payment);

  readonly setEmptyTemplate = this.updater((state, value: string) => ({ ...state, emptyTemplate: value }));

  readonly setFirstLoadErrorType = this.updater((state, value: string) => ({ ...state, firstLoadErrorType: value }));

  readonly setPayment = this.effect((params$: Observable<{ payment: Payment }>) => {
    return params$.pipe(
      tap((params: { payment: Payment }) => {
        const payment = params.payment;
        const paymentViewModel: PaymentViewModel = {
          ...payment,
          contact: this.get().publisherViewModels.find(
            publisherViewModel => publisherViewModel.id === payment.contact.id
          )
        };

        const publisher = payment.contact;
        this.store.dispatch(new AddPublishers({ publishers: [publisher] }));

        this.setState((state) => {
          return {
            ...state,
            payment,
            paymentViewModel
          };
        });
      })
    );
  });

  readonly updatePayment = this.effect((params$: Observable<{ payment: Payment }>) => {
    return params$.pipe(
      tap((params: { payment: Payment }) => {
        this.setPayment({ payment: params.payment });
      })
    );
  });

  readonly loadPayment = this.effect(
    (params$: Observable<{ schoolId: number; studentId: number; paymentId: number; loadingFromList: boolean }>) => {
      return params$.pipe(
        withLatestFrom(this.studentPaymentsStore.state$),
        withLatestFrom(this.store.select(fromStore.selectAllPaymentIntents)),
        tap(([[params, state], paymentIntents]) => {
          let paymentBeingConfirmed: boolean;

          this.setEmptyTemplate('firstLoad');

          if (
            paymentIntents.findIndex(
              paymentIntent =>
                paymentIntent?.paymentId === params?.paymentId && paymentIntent?.studentId === params?.studentId
            ) === -1
          ) {
            paymentBeingConfirmed = false;
          } else {
            paymentBeingConfirmed = true;
          }

          if (params.loadingFromList) {
            const paymentFromList = state.payments.find(
              payment => payment.id === params.paymentId && payment.studentId === params.studentId
            );

            if (paymentFromList) {
              this.setPayment({ payment: { ...paymentFromList, paymentBeingConfirmed } });

              this.setEmptyTemplate(null);
            }
          } else {
            const timer = interval(1000).pipe(take(1));
            const request = this.paymentService
              .loadPayment(params.schoolId, params.studentId, params.paymentId)
              .pipe(catchError(error => of(error)));

            forkJoin({
              timer,
              request
            }).subscribe((result) => {
              if (result.request instanceof HttpErrorResponse || result.request instanceof TimeoutError) {
                console.log('[HTTP REQUEST] Load Payment Error', result.request);

                if (result.request instanceof HttpErrorResponse) {
                  switch (result.request.status) {
                    case 0:
                      this.setFirstLoadErrorType('noInternet');
                      this.showToast('Please check your internet connection and try again later', 'noInternetToast');
                      break;

                    case 404:
                      this.setFirstLoadErrorType('notFound');
                      break;

                    default:
                      this.setFirstLoadErrorType('somethingWrong');
                      break;
                  }
                } else {
                  this.setFirstLoadErrorType('somethingWrong');
                }

                this.setEmptyTemplate('firstLoadError');
              } else {
                console.log('[HTTP REQUEST] Load Payment Success', result.request);

                this.setPayment({ payment: { ...result.request, paymentBeingConfirmed } });

                this.studentPaymentsStore.updatePayment({ payment: result.request, studentId: params.studentId });

                this.setEmptyTemplate(null);
              }
            });
          }
        })
      );
    }
  );

  showToast(toastMessage: string, toastCssClass: string): void {
    const toast = this.toastController.create({
      message: toastMessage,
      duration: 2000,
      position: 'top',
      cssClass: toastCssClass
    });

    toast.then(t => t.present());
  }
}
